import { type LoadedStem, type StemRole, type StemSource } from "@repo/audio-engine";

export const CORE_ROLES: readonly StemRole[] = [
  "vocals",
  "drums",
  "bass",
  "guitar",
  "piano",
  "other",
];

interface SongLike {
  readonly youtube_id?: unknown;
  readonly youtubeId?: unknown;
  readonly title?: unknown;
  readonly status?: unknown;
  readonly stems?: unknown;
  readonly smams_url?: unknown;
  readonly smamsUrl?: unknown;
}

interface SongSearchResponse {
  readonly songs?: unknown;
}

interface SongBankResponse {
  readonly songs?: unknown;
}

interface CheckSongResponse {
  readonly action?: unknown;
  readonly song_data?: unknown;
}

interface CheckSongData {
  readonly title?: unknown;
  readonly stems?: unknown;
  readonly smams_url?: unknown;
}

export interface LoadedBackendSong {
  readonly youtubeId: string;
  readonly title: string;
  readonly stems: readonly LoadedStem[];
  readonly smamsUrl?: string;
  readonly stemUrlsByRole: Readonly<Partial<Record<StemRole, string>>>;
}

export interface StemLoadProgress {
  readonly role: StemRole;
  readonly phase: "downloading" | "decoding" | "ready";
  readonly loadedBytes: number;
  readonly totalBytes?: number;
}

export interface LoadRandomBackendSongOptions {
  readonly onStemProgress?: (progress: StemLoadProgress) => void;
  readonly signal?: AbortSignal;
}

interface RandomSongCandidate {
  readonly youtubeId: string;
  readonly title: string;
  readonly stems: Readonly<Partial<Record<StemRole, string>>>;
  readonly smamsUrl?: string;
}

export interface BackendSongReference {
  readonly youtubeId: string;
  readonly title?: string;
  readonly smamsUrl?: string;
  readonly stemUrlsByRole?: Readonly<Partial<Record<StemRole, string>>>;
}

export interface RandomBackendSongDescriptor {
  readonly youtubeId: string;
  readonly title: string;
  readonly smamsUrl?: string;
  readonly stemUrlsByRole: Readonly<Partial<Record<StemRole, string>>>;
}

const LEGACY_SONG_CATALOG_BASE_URL =
  "https://pj724ne7xj.execute-api.ap-southeast-2.amazonaws.com/dev";
const CORS_FRIENDLY_SONG_CATALOG_BASE_URL = "https://api-dev.sheetmuse.ai";

function createAbortError(): Error {
  if (typeof DOMException !== "undefined") {
    return new DOMException("Song load aborted", "AbortError");
  }
  const error = new Error("Song load aborted");
  (error as Error & { name: string }).name = "AbortError";
  return error;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw createAbortError();
  }
}

function ensureAbsoluteApiUrl(baseUrl: string, url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  if (url.startsWith("/")) return `${normalizedBase}${url}`;
  return `${normalizedBase}/${url}`;
}

function toSongLike(raw: unknown): SongLike | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  return raw;
}

function stemMap(raw: unknown): Partial<Record<StemRole, string>> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  const record = raw as Record<string, unknown>;
  const out: Partial<Record<StemRole, string>> = {};
  for (const role of CORE_ROLES) {
    const value = record[role];
    if (typeof value === "string" && value.length > 0) out[role] = value;
  }
  return out;
}

function secureRandomIndex(length: number): number {
  if (length <= 0) return 0;
  const word = new Uint32Array(1);
  crypto.getRandomValues(word);
  const value = word[0];
  if (value === undefined) return 0;
  return value % length;
}

async function fetchSongSearchRandom(
  apiBaseUrl: string,
  limit: number,
  signal?: AbortSignal,
): Promise<readonly RandomSongCandidate[]> {
  throwIfAborted(signal);
  const listUrl = `${apiBaseUrl.replace(/\/$/, "")}/api/v1/song-search?limit=${limit}&random=1`;
  const response = await fetch(listUrl, signal ? { signal } : undefined);
  if (!response.ok) return [];
  const payload = (await response.json()) as SongSearchResponse;
  const songsRaw = Array.isArray(payload.songs) ? payload.songs : [];
  const songs: RandomSongCandidate[] = [];
  for (const itemRaw of songsRaw) {
    const item = toSongLike(itemRaw);
    if (item === null) continue;
    const youtubeId =
      typeof item.youtube_id === "string"
        ? item.youtube_id
        : typeof item.youtubeId === "string"
          ? item.youtubeId
          : "";
    if (!youtubeId) continue;
    songs.push({
      youtubeId,
      title: typeof item.title === "string" && item.title ? item.title : "Unknown Title",
      stems: stemMap(item.stems),
      ...(typeof item.smams_url === "string"
        ? { smamsUrl: item.smams_url }
        : typeof item.smamsUrl === "string"
          ? { smamsUrl: item.smamsUrl }
          : {}),
    });
  }
  return songs;
}

async function fetchSongBankFallback(
  apiBaseUrl: string,
  limit: number,
  signal?: AbortSignal,
): Promise<readonly RandomSongCandidate[]> {
  throwIfAborted(signal);
  const listUrl = `${apiBaseUrl.replace(/\/$/, "")}/api/v1/song-bank?limit=${limit}`;
  const response = await fetch(listUrl, signal ? { signal } : undefined);
  if (!response.ok) return [];
  const payload = (await response.json()) as SongBankResponse;
  const songsRaw = Array.isArray(payload.songs) ? payload.songs : [];
  const songs: RandomSongCandidate[] = [];
  for (const itemRaw of songsRaw) {
    const item = toSongLike(itemRaw);
    if (item === null) continue;
    const status = typeof item.status === "string" ? item.status : "completed";
    if (status !== "completed") continue;
    const youtubeId =
      typeof item.youtube_id === "string"
        ? item.youtube_id
        : typeof item.youtubeId === "string"
          ? item.youtubeId
          : "";
    if (!youtubeId) continue;
    songs.push({
      youtubeId,
      title: typeof item.title === "string" && item.title ? item.title : "Unknown Title",
      stems: stemMap(item.stems),
      ...(typeof item.smams_url === "string"
        ? { smamsUrl: item.smams_url }
        : typeof item.smamsUrl === "string"
          ? { smamsUrl: item.smamsUrl }
          : {}),
    });
  }
  return songs;
}

function withSynthesizedStemUrls(
  apiBaseUrl: string,
  youtubeId: string,
  stems: Readonly<Partial<Record<StemRole, string>>>,
): Readonly<Partial<Record<StemRole, string>>> {
  const hydrated: Partial<Record<StemRole, string>> = {};
  for (const role of CORE_ROLES) {
    const maybe = stems[role];
    if (maybe) {
      hydrated[role] = ensureAbsoluteApiUrl(apiBaseUrl, maybe);
      continue;
    }
    hydrated[role] =
      `${apiBaseUrl.replace(/\/$/, "")}/api/v1/file-proxy/${encodeURIComponent(youtubeId)}/${role}`;
  }
  return hydrated;
}

async function hydrateWithCheckSong(
  apiBaseUrl: string,
  candidate: RandomSongCandidate,
  signal?: AbortSignal,
): Promise<RandomSongCandidate> {
  throwIfAborted(signal);
  const checkUrl = `${apiBaseUrl.replace(/\/$/, "")}/api/v1/check-song?youtube_id=${encodeURIComponent(candidate.youtubeId)}`;
  const response = await fetch(checkUrl, signal ? { signal } : undefined);
  if (!response.ok) {
    return {
      ...candidate,
      stems: withSynthesizedStemUrls(apiBaseUrl, candidate.youtubeId, candidate.stems),
    };
  }
  const payload = (await response.json()) as CheckSongResponse;
  if (payload.action !== "PLAY_NOW") {
    return {
      ...candidate,
      stems: withSynthesizedStemUrls(apiBaseUrl, candidate.youtubeId, candidate.stems),
    };
  }
  const songData = payload.song_data as CheckSongData | undefined;
  const hydratedStems = stemMap(songData?.stems);
  const mergedStems = withSynthesizedStemUrls(
    apiBaseUrl,
    candidate.youtubeId,
    Object.keys(hydratedStems).length > 0 ? hydratedStems : candidate.stems,
  );
  const nextTitle =
    typeof songData?.title === "string" && songData.title.length > 0
      ? songData.title
      : candidate.title;
  const nextSmams =
    typeof songData?.smams_url === "string" && songData.smams_url.length > 0
      ? ensureAbsoluteApiUrl(apiBaseUrl, songData.smams_url)
      : candidate.smamsUrl
        ? ensureAbsoluteApiUrl(apiBaseUrl, candidate.smamsUrl)
        : undefined;
  return {
    youtubeId: candidate.youtubeId,
    title: nextTitle,
    stems: mergedStems,
    ...(nextSmams ? { smamsUrl: nextSmams } : {}),
  };
}

async function fetchSongByYoutubeId(
  apiBaseUrl: string,
  youtubeId: string,
  signal?: AbortSignal,
): Promise<RandomSongCandidate> {
  const minimalCandidate: RandomSongCandidate = {
    youtubeId,
    title: "Unknown",
    stems: {},
  };
  return hydrateWithCheckSong(apiBaseUrl, minimalCandidate, signal);
}

async function fetchRandomSongCandidate(
  apiBaseUrl: string,
  signal?: AbortSignal,
): Promise<RandomSongCandidate> {
  const baseCandidates = [
    ...new Set([apiBaseUrl, CORS_FRIENDLY_SONG_CATALOG_BASE_URL, LEGACY_SONG_CATALOG_BASE_URL]),
  ];
  const errors: string[] = [];

  for (const base of baseCandidates) {
    try {
      throwIfAborted(signal);
      const fromSearch = await fetchSongSearchRandom(base, 20, signal);
      const candidates =
        fromSearch.length > 0 ? fromSearch : await fetchSongBankFallback(base, 20, signal);
      if (candidates.length === 0) {
        errors.push(`${base}: no candidates from song-search/song-bank`);
        continue;
      }
      const picked = candidates[secureRandomIndex(candidates.length)];
      if (!picked) {
        errors.push(`${base}: random pick failed`);
        continue;
      }
      return await hydrateWithCheckSong(base, picked, signal);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      errors.push(`${base}: ${message}`);
    }
  }

  throw new Error(
    `Unable to load random song candidates from configured catalog endpoints. ${errors.join(" | ")}`,
  );
}

export async function loadRandomBackendSong(
  ctx: BaseAudioContext,
  apiBaseUrl: string,
  options?: LoadRandomBackendSongOptions,
): Promise<LoadedBackendSong> {
  const descriptor = await resolveRandomBackendSongDescriptor(apiBaseUrl, options?.signal);
  return loadBackendSongByReference(
    ctx,
    apiBaseUrl,
    {
      youtubeId: descriptor.youtubeId,
      title: descriptor.title,
      ...(descriptor.smamsUrl ? { smamsUrl: descriptor.smamsUrl } : {}),
      stemUrlsByRole: descriptor.stemUrlsByRole,
    },
    options,
  );
}

export async function resolveRandomBackendSongDescriptor(
  apiBaseUrl: string,
  signal?: AbortSignal,
): Promise<RandomBackendSongDescriptor> {
  const candidate = await fetchRandomSongCandidate(apiBaseUrl, signal);
  return {
    youtubeId: candidate.youtubeId,
    title: candidate.title,
    stemUrlsByRole: candidate.stems,
    ...(candidate.smamsUrl ? { smamsUrl: candidate.smamsUrl } : {}),
  };
}

export async function loadBackendSongByYoutubeId(
  ctx: BaseAudioContext,
  apiBaseUrl: string,
  youtubeId: string,
  options?: LoadRandomBackendSongOptions,
): Promise<LoadedBackendSong> {
  const candidate = await fetchSongByYoutubeId(apiBaseUrl, youtubeId, options?.signal);
  const sources: StemSource[] = [];
  for (const role of CORE_ROLES) {
    const url = candidate.stems[role];
    if (!url) continue;
    sources.push({ role, url });
  }
  if (sources.length === 0) {
    throw new Error(`Song ${youtubeId} resolved with zero playable stem URLs`);
  }
  const loaded = await loadDecodedStems(ctx, sources, options?.onStemProgress, options?.signal);
  return {
    youtubeId: candidate.youtubeId,
    title: candidate.title,
    stems: loaded,
    stemUrlsByRole: candidate.stems,
    ...(candidate.smamsUrl ? { smamsUrl: candidate.smamsUrl } : {}),
  };
}

export async function loadBackendSongByReference(
  ctx: BaseAudioContext,
  apiBaseUrl: string,
  reference: BackendSongReference,
  options?: LoadRandomBackendSongOptions,
): Promise<LoadedBackendSong> {
  const normalizedYoutubeId = reference.youtubeId.trim();
  if (!normalizedYoutubeId) {
    throw new Error("Song reference missing youtubeId");
  }
  const providedStemUrls = reference.stemUrlsByRole ?? {};
  let hasAnyProvidedStem = false;
  for (const role of CORE_ROLES) {
    const value = providedStemUrls[role];
    if (typeof value === "string" && value.trim().length > 0) {
      hasAnyProvidedStem = true;
      break;
    }
  }
  if (!hasAnyProvidedStem) {
    return loadBackendSongByYoutubeId(ctx, apiBaseUrl, normalizedYoutubeId, options);
  }
  const candidate: RandomSongCandidate = {
    youtubeId: normalizedYoutubeId,
    title:
      reference.title && reference.title.trim().length > 0 ? reference.title.trim() : "Unknown",
    stems: withSynthesizedStemUrls(apiBaseUrl, normalizedYoutubeId, providedStemUrls),
    ...(reference.smamsUrl && reference.smamsUrl.trim().length > 0
      ? { smamsUrl: reference.smamsUrl.trim() }
      : {}),
  };
  const sources: StemSource[] = [];
  for (const role of CORE_ROLES) {
    const url = candidate.stems[role];
    if (!url) continue;
    sources.push({ role, url });
  }
  if (sources.length === 0) {
    throw new Error(`Song ${normalizedYoutubeId} reference resolved with zero playable stem URLs`);
  }
  const loaded = await loadDecodedStems(ctx, sources, options?.onStemProgress, options?.signal);
  return {
    youtubeId: candidate.youtubeId,
    title: candidate.title,
    stems: loaded,
    stemUrlsByRole: candidate.stems,
    ...(candidate.smamsUrl ? { smamsUrl: candidate.smamsUrl } : {}),
  };
}

async function loadDecodedStems(
  ctx: BaseAudioContext,
  sources: readonly StemSource[],
  onProgress?: (progress: StemLoadProgress) => void,
  signal?: AbortSignal,
): Promise<LoadedStem[]> {
  const bytesBySource = await Promise.all(
    sources.map(async (source) => {
      throwIfAborted(signal);
      const bytes = await fetchStemBytes(source, onProgress, signal);
      throwIfAborted(signal);
      if (onProgress !== undefined) {
        onProgress({
          role: source.role,
          phase: "decoding",
          loadedBytes: bytes.byteLength,
          totalBytes: bytes.byteLength,
        });
      }
      return { source, bytes };
    }),
  );

  const loaded = await Promise.all(
    bytesBySource.map(async ({ source, bytes }) => {
      throwIfAborted(signal);
      const buffer = await ctx.decodeAudioData(bytes);
      throwIfAborted(signal);
      if (onProgress !== undefined) {
        onProgress({
          role: source.role,
          phase: "ready",
          loadedBytes: bytes.byteLength,
          totalBytes: bytes.byteLength,
        });
      }
      return { role: source.role, url: source.url, buffer };
    }),
  );

  return loaded;
}

async function fetchStemBytes(
  source: StemSource,
  onProgress?: (progress: StemLoadProgress) => void,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  throwIfAborted(signal);
  const response = await fetch(source.url, {
    credentials: "omit",
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    throw new Error(`stem fetch failed: ${source.url} -> ${response.status}`);
  }
  const headersValue = (response as unknown as { headers?: unknown }).headers;
  let totalHeader: string | null = null;
  if (typeof headersValue === "object" && headersValue !== null && "get" in headersValue) {
    totalHeader = (headersValue as { get: (name: string) => string | null }).get("content-length");
  }
  const totalBytes =
    totalHeader !== null && Number.isFinite(Number(totalHeader)) ? Number(totalHeader) : undefined;
  const stream = response.body;
  if (!stream) {
    const bytes = await response.arrayBuffer();
    onProgress?.({
      role: source.role,
      phase: "downloading",
      loadedBytes: bytes.byteLength,
      ...(totalBytes !== undefined ? { totalBytes } : {}),
    });
    return bytes;
  }

  const reader = stream.getReader();
  let loadedBytes = 0;
  const fixedBuffer =
    totalBytes !== undefined && totalBytes > 0 ? new Uint8Array(totalBytes) : undefined;
  const chunks: Uint8Array[] = fixedBuffer === undefined ? [] : [];
  onProgress?.({
    role: source.role,
    phase: "downloading",
    loadedBytes: 0,
    ...(totalBytes !== undefined ? { totalBytes } : {}),
  });
  for (;;) {
    throwIfAborted(signal);
    const result = await reader.read();
    if (result.done) {
      break;
    }
    const chunk = result.value;
    if (fixedBuffer !== undefined) {
      const nextLoadedBytes = loadedBytes + chunk.byteLength;
      const clampedEnd = Math.min(nextLoadedBytes, fixedBuffer.byteLength);
      fixedBuffer.set(chunk.subarray(0, clampedEnd - loadedBytes), loadedBytes);
      loadedBytes = clampedEnd;
    } else {
      chunks.push(chunk);
      loadedBytes += chunk.byteLength;
    }
    onProgress?.({
      role: source.role,
      phase: "downloading",
      loadedBytes,
      ...(totalBytes !== undefined ? { totalBytes } : {}),
    });
  }
  if (fixedBuffer !== undefined) {
    return loadedBytes === fixedBuffer.byteLength
      ? fixedBuffer.buffer
      : fixedBuffer.slice(0, loadedBytes).buffer;
  }
  const joined = new Uint8Array(loadedBytes);
  let offset = 0;
  for (const chunk of chunks) {
    joined.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return joined.buffer;
}
